import { a as __rest } from '../../../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import React__default, { forwardRef, useMemo, memo } from 'react';
import { Image } from '../../../Image/Image.esm.js';
import { useCardContext } from '../../Card.context.esm.js';
import { addClassNameToChild, isImageConfig } from '../../utils/image.esm.js';
import imageStyles from './CardImage.styles.esm.js';
import { DEFAULT_CARD_IMAGE_CONFIG } from './constants.esm.js';
import { cn } from '../../../../utils/cn.esm.js';

function renderCardImage(image, baseClassName) {
    if (!image)
        return null;
    // React element passed directly
    if (React__default.isValidElement(image)) {
        const element = image;
        return React__default.cloneElement(element, {
            className: cn(element.props.className || "", baseClassName),
        });
    }
    // String => treat as src
    if (typeof image === "string") {
        return (jsx(Image, { src: image, alt: DEFAULT_CARD_IMAGE_CONFIG.alt, width: DEFAULT_CARD_IMAGE_CONFIG.width, height: DEFAULT_CARD_IMAGE_CONFIG.height, className: baseClassName }));
    }
    // Config object
    if (isImageConfig(image)) {
        const { src, alt = DEFAULT_CARD_IMAGE_CONFIG.alt, width = DEFAULT_CARD_IMAGE_CONFIG.width, height = DEFAULT_CARD_IMAGE_CONFIG.height, loading, className, } = image;
        return (jsx(Image, { src: src, alt: alt, width: width, height: height, loading: loading, className: cn(baseClassName, className) }));
    }
    return null;
}
const CardImageBase = forwardRef((_a, ref) => {
    var { className, children } = _a, imageProps = __rest(_a, ["className", "children"]);
    const { imagePosition } = useCardContext();
    const classes = useMemo(() => imageStyles({ imagePosition }), [imagePosition]);
    const containerClass = cn(classes.imageContainer(), className);
    const imageClass = classes.image();
    // Compute rendered image once per relevant changes
    const rendered = useMemo(() => renderCardImage(imageProps, imageClass), [imageProps, imageClass]);
    const content = children != null
        ? Array.isArray(children)
            ? children.map((c, i) => (jsx(React__default.Fragment, { children: addClassNameToChild(c, imageClass) }, i)))
            : addClassNameToChild(children, imageClass)
        : (rendered !== null && rendered !== void 0 ? rendered : null);
    if (!content)
        return null;
    const containerStyle = imageProps.width || imageProps.height
        ? { width: imageProps.width, height: imageProps.height }
        : undefined;
    return (jsx("div", { ref: ref, className: containerClass, style: containerStyle, children: content }));
});
CardImageBase.displayName = "CardImage";
const CardImage = Object.assign(memo(CardImageBase), {
    __CARD_IMAGE: true,
    displayName: "CardImage",
});

export { CardImage };
